// src/types/lote/OperacaoLote.ts
//
// Tipos do motor de operações em lote do backend api-blueltSystem (issue #56,
// ADR-037). Espelham:
//   - GET /v1/operacoes-lote/{id}  (polling do andamento)
//   - o ramo 202 das 8 rotas que processam em segundo plano
//
// Contrato verificado no código do backend, não na issue #31 (que tinha o nome
// do enum errado):
//   common/dto/response/OperacaoLoteResponseDTO.java
//   common/enuns/StatusOperacaoLote.java
//   common/enuns/AcaoLote.java

// Ciclo de vida de uma operação em lote.
// EM_ANDAMENTO é o único estado que segura a trava (acao, chavePai) no backend;
// os outros três são terminais.
export type StatusOperacaoLote =
  | 'EM_ANDAMENTO'
  | 'CONCLUIDO'
  // Terminou, mas parte dos itens foi ignorada pelas regras que já existiam
  // (item de outro conferente, item bloqueado no reverter). NÃO é erro — é o
  // soft-fail por item que a ação síncrona já tinha. É também o único lugar
  // onde o skip silencioso fica visível ao front (via totalIgnorado).
  | 'CONCLUIDO_COM_FALHAS'
  | 'FALHOU';

// Ações que o motor sabe serializar. O nome é o do CÓDIGO do backend, nunca o
// rótulo de tela — APROVAR cobre Aprovar E Reprovar (mesmo endpoint, decisão em
// decisaoPadrao), e REVERTER cobre Desaprovar E Revogar (abas do mesmo endpoint).
export type AcaoLote =
  | 'REVERTER'        // PUT  /v1/requisicoes/{id}/itens/aprovar/reverter
  | 'EXCLUIR'         // POST /v1/recebimento-conferencia/item/excluir
  | 'APROVAR'         // PUT  /v1/requisicoes/{id}/itens/aprovar
  | 'EDITAR_QTD'      // POST /v1/recebimento-conferencia/item/editar-qtd
  | 'REMOVER'         // PUT  /v1/pre-ordens/{id}/devolver-itens
  | 'DEVOLVER'        // POST /v1/requisicoes/itens/devolver
  | 'ADICIONAR'       // POST /v1/recebimento-conferencia/{recebimentoConferenciaId}/itens/adicionar
  | 'CRIAR_REQUISICAO'; // POST /v1/requisicoes

// Estados terminais — o polling para quando o status é um destes.
const STATUS_TERMINAIS: readonly StatusOperacaoLote[] = [
  'CONCLUIDO',
  'CONCLUIDO_COM_FALHAS',
  'FALHOU',
];

export const isStatusTerminal = (status: StatusOperacaoLote): boolean =>
  STATUS_TERMINAIS.includes(status);

// Corpo do 202 e da resposta do polling (GET /v1/operacoes-lote/{id}).
//
// Sem percentual por desenho: o job roda em UMA transação (é o que preserva o
// tudo-ou-nada), então o polling devolve EM_ANDAMENTO até um estado terminal.
// A UX é "avisamos quando terminar", nunca uma barra de progresso.
//
// chavePai é comparada, nunca parseada — para CRIAR_REQUISICAO ela é a chave
// composta (nroContato, codFilial) serializada numa string, e o backend documenta
// que nada lê esse valor de volta.
export interface OperacaoLoteResponse {
  id: number;
  acao: AcaoLote;
  chavePai: string;
  status: StatusOperacaoLote;
  totalResolvido: number | null;
  totalProcessado: number | null;
  // Itens que saíram do conjunto pelas regras de sempre. O chamador sabe QUANTOS
  // foram, nunca QUAIS — e só no caminho assíncrono; o inline não tem esse dado.
  totalIgnorado: number | null;
  iniciadoEm: string;              // ISO LocalDateTime
  atualizadoEm: string | null;
  finalizadoEm: string | null;
  erroMensagem: string | null;
}
